/**
 * Application Technical Configuration
 *
 * In deployed environments, config is loaded from /config.json (generated by CDK at deploy time).
 * In local development, config falls back to the default values below.
 *
 * Use resources.ts for UI strings and labels.
 */

/** Identification rule shape, matching the backend IdentificationRule model. */
export interface IdentificationRule {
  pathPrefix: string;
  fileType: string;
}

/** Default file types when no runtime config is provided (e.g. local dev). */
export const DEFAULT_FILE_TYPES: string[] = [
  "OFSTED_INSPECTION_OUTCOMES",
  "NHS_PRACTICE_CURRENT",
  "NHS_PRACTICE_PATIENTS",
  "GP_PRACTICES",
  "GP_PRACTITIONERS",
  "GP_PATIENTS_PER_PRACTICE",
  "HEALTH_PROVIDERS",
  "ORDNANCE_SURVEY_ADDRESS_CORE",
  "GEO_LOCATIONS",
  "POSTCODES_LAT_LNG",
  "POLICE_CRIMES",
  "EDU_PROVIDERS",
  "TEST_FILE",
  "UNKNOWN",
];

/** Default identification rules when no runtime config is provided (e.g. local dev). */
export const DEFAULT_IDENTIFICATION_RULES: IdentificationRule[] = [
  { pathPrefix: "ofsted", fileType: "OFSTED_INSPECTION_OUTCOMES" },
  { pathPrefix: "health/nhs/epraccur", fileType: "NHS_PRACTICE_CURRENT" },
  { pathPrefix: "health/nhs/gp-reg-pat-prac-quin-age", fileType: "NHS_PRACTICE_PATIENTS" },
  { pathPrefix: "health/gp/practices", fileType: "GP_PRACTICES" },
  { pathPrefix: "health/gp/practitioners", fileType: "GP_PRACTITIONERS" },
  { pathPrefix: "health/gp/patients", fileType: "GP_PATIENTS_PER_PRACTICE" },
  { pathPrefix: "health_providers", fileType: "HEALTH_PROVIDERS" },
  { pathPrefix: "os-address-core", fileType: "ORDNANCE_SURVEY_ADDRESS_CORE" },
  { pathPrefix: "locations", fileType: "GEO_LOCATIONS" },
  { pathPrefix: "postcodes", fileType: "POSTCODES_LAT_LNG" },
  { pathPrefix: "police/crimes", fileType: "POLICE_CRIMES" },
  { pathPrefix: "education", fileType: "EDU_PROVIDERS" },
  { pathPrefix: "test", fileType: "TEST_FILE" },
];

export interface AppConfig {
  api: {
    baseUrl: string;
    timeout: number;
    useMock: boolean;
  };
  auth: {
    authority: string;
    clientId: string;
    scopes: string;
    redirectUri: string;
    postLogoutRedirectUri: string;
  };
  environment: {
    name: string;
    version: string;
    isDebug: boolean;
  };
}

/** Runtime overlay from /config.json (CDK-deployed). Matches HarbourRuntimeConfig in @axetay/axetay-harbour. */
export type RuntimeConfigOverlay = {
  api?: Partial<AppConfig["api"]>;
  auth?: Partial<AppConfig["auth"]>;
  environment?: Partial<AppConfig["environment"]>;
};

const defaultConfig: AppConfig = {
  api: {
    baseUrl: "https://api.harbour.axetay.com/v1",
    timeout: 10000,
    useMock: true,
  },
  auth: {
    /*     "oidcAuthority": "https://cognito-idp.eu-west-2.amazonaws.com/eu-west-2_dj4iLv0cs",
    "oidcClientId": "svea1d3tsunrrjhf7p2dpj14g"
 */
    authority:
      "https://cognito-idp.eu-west-2.amazonaws.com/eu-west-2_dj4iLv0cs",
    clientId: "svea1d3tsunrrjhf7p2dpj14g",
    scopes: "openid profile email",
    redirectUri: typeof window !== "undefined" ? window.location.origin : "",
    postLogoutRedirectUri:
      typeof window !== "undefined" ? window.location.origin : "",
  },
  environment: {
    name: "development",
    version: "1.0.4",
    isDebug: true,
  },
};

let _config: AppConfig = { ...defaultConfig };

/**
 * Loads runtime configuration from /config.json.
 * Falls back to default config if the file doesn't exist (local dev).
 * Must be called before the app renders.
 */
export async function loadConfig(): Promise<AppConfig> {
  try {
    const response = await fetch("/config.json");
    if (response.ok) {
      const runtimeConfig: RuntimeConfigOverlay = await response.json();
      _config = {
        api: { ...defaultConfig.api, ...runtimeConfig.api },
        auth: { ...defaultConfig.auth, ...runtimeConfig.auth },
        environment: {
          ...defaultConfig.environment,
          ...runtimeConfig.environment,
        },
      };
    }
  } catch {
    // In development, config.json won't exist -- use defaults
    console.log("No runtime config found, using defaults");
  }
  return _config;
}

export const config: AppConfig = new Proxy(defaultConfig, {
  get(_target, prop: string) {
    return _config[prop as keyof AppConfig];
  },
}) as AppConfig;

/** True when runtime has provided an OIDC authority and client (e.g. from /config.json). */
export function isAuthConfigured(): boolean {
  return Boolean(_config.auth.authority && _config.auth.clientId);
}
